import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Col, Row, message } from 'antd';

import { useTranslation } from 'react-i18next';
import POSButton from '../POSButton';
import POSContext from '../POSContext';

import {
  cancelOrder,
  getAvailableCards,
  setOrderCardType,
} from '../../../services/pos/pos';
import useCard from '../../../hooks/useCard';
import { getEnrolledBankProduct } from '../../../services/pos/enroll-bank-cards';
import { showMessage } from '../../../helpers/printing';
import { getEnrolledProduct } from '../../../services/stocks/cards';
import { BULK_TYPES } from '../../../pages/orders/constants';

const SelectCard = () => {
  const { shift, order, setOrder } = useContext(POSContext);
  const { t } = useTranslation();
  const [cards, setCards] = useState([]);
  const [productId, setProductId] = useState(null);
  const [bankCard, setBankCard] = useState(false);

  const handleEnrollExternalCard = useCallback(
    async (physicalId) => {
      if (physicalId && physicalId !== 0) {
        try {
          const product = await getEnrolledProduct(physicalId);
          if (product.status === 'ENROLLED') {
            const crd = {
              productId,
              physicalId,
              logicalId: product?.serial,
            };

            setOrderCardType(shift, order, { ...crd })
              .then((res) => setOrder(res))
              .catch((err) => {
                console.error(err);
                message.error({
                  content: t('entity.pos.errors.invalidType'),
                  key: 'pos',
                  duration: 5,
                  className: 'card-message',
                });
              });
          } else if (product.status === 'BLOCKED') {
            showMessage('error', t('errors.blockedCardEnrolled'));
          } else if (product.status === 'SOLD') {
            showMessage('error', t('errors.soldCardEnroll'));
          } else {
            showMessage('error', t('errors.errorCardEnroll'));
          }
        } catch (err) {
          const { inner } = err || {};
          const { _: messageCode } = inner || {};
          showMessage(
            'error',
            messageCode ? t(messageCode) : t('errors.errorOccurred'),
          );
        }
      }
    },
    [order, setOrder, shift, t, productId],
  );

  const handleBankCard = useCallback(
    async (physicalId) => {
      if (physicalId && physicalId !== 0) {
        try {
          const product = await getEnrolledBankProduct(
            physicalId,
            BULK_TYPES.ISSUING_CARDS,
          );
          const crd = {
            productId,
            physicalId,
            logicalId: product?.logicIdSerial,
          };

          setOrderCardType(shift, order, { ...crd })
            .then((res) => setOrder(res))
            .catch((err) => {
              console.error(err);
              message.error({
                content: t('entity.pos.errors.invalidType'),
                key: 'pos',
                duration: 5,
                className: 'card-message',
              });
            });
        } catch (err) {
          const { inner } = err || {};
          const { _: messageCode } = inner || {};
          showMessage(
            'error',
            messageCode ? t(messageCode) : t('errors.errorOccurred'),
          );
        }
      }
    },
    [order, setOrder, shift, t, productId],
  );

  const onCardScan = useCallback(() => {
    message.loading({
      content: t('entity.pos.cardIdentification'),
      key: 'pos',
      duration: 0,
      className: 'card-message',
    });
  }, [t]);

  const onCardFound = useCallback(
    (card) => {
      message.success({
        // content: `Card identificat: ${card}`,
        content: t('errors.identifiedCard', { card }),
        key: 'pos',
        duration: 1,
        className: 'card-message',
      });
      const processCard = (cardId) =>
        bankCard ? handleBankCard(cardId) : handleEnrollExternalCard(cardId);
      processCard(card);
    },
    [handleEnrollExternalCard, handleBankCard, bankCard, t],
  );

  const onCardMissing = useCallback(() => {
    message.error({
      content: t('errors.cardNotFound'),
      key: 'pos',
      duration: 5,
      className: 'card-message',
    });
  }, [t]);

  const [, , fetchCard, cancelFetchCard] = useCard({
    onCardScan,
    onCardFound,
    onCardMissing,
  });

  useEffect(() => {
    getAvailableCards(shift, order)
      .then((c) => setCards(c))
      .catch((err) => {
        console.error(err);
        setCards([]);
      });
  }, [shift, order]);

  const handleSelectCard = useCallback(
    (selectedCard) => {
      if (selectedCard.externalCardType) {
        setProductId(selectedCard?.productId);
        setBankCard(selectedCard.name === 'PAN');
        fetchCard();
      } else {
        cancelFetchCard();
        setOrderCardType(shift, order, { ...selectedCard })
          .then((res) => setOrder(res))
          .catch((err) => {
            console.error(err);
            message.error({
              content: t('errors.unusableType'),
              key: 'pos',
              duration: 5,
              className: 'card-message',
            });
          });
      }
    },
    [order, setOrder, shift, fetchCard, cancelFetchCard, t],
  );

  const handleCancelOrder = useCallback(() => {
    cancelOrder(shift, order)
      .then((res) => {
        setOrder(res);
      })
      .catch((err) => {
        console.error(err);
        message.error({
          content: t('entity.pos.errors.cancelOrderError'),
          key: 'pos',
          duration: 5,
          className: 'card-message',
        });
      });
  }, [shift, order, setOrder, t]);

  return (
    <div className="pos">
      <div className="pos__wrapper">
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32, xl: 40 }}>
          {(cards || []).map((card) => (
            <Col span={4} key={card.id}>
              <POSButton
                title={card.name}
                color="green"
                onClick={() =>
                  handleSelectCard({
                    productId: card.id,
                    name: card.name,
                    externalCardType: card.external,
                  })
                }
              />
            </Col>
          ))}
        </Row>
      </div>
      <div className="pos__actions">
        <div>
          <POSButton
            title={t('entity.pos.actions.cancel')}
            color="red"
            size="small"
            onClick={handleCancelOrder}
          />
        </div>

        <div>&nbsp;</div>
      </div>
    </div>
  );
};

export default SelectCard;
